import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { recordRenderCrash } from "@/lib/client-events";

interface Props {
  children: ReactNode;
  /** Optional fallback UI. When omitted, uses the built-in error card. */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Global error boundary. Catches rendering errors and chunk-load failures
 * (e.g. network issues during lazy() imports) and shows a friendly retry UI
 * instead of white-screening the entire app.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[ErrorBoundary] Uncaught error:", error, errorInfo);
    try {
      recordRenderCrash(error);
    } catch {
      // The sink promises not to throw, but a telemetry regression must never
      // replace the boundary's recovery UI with a second exception.
    }
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      const isChunkError =
        this.state.error?.message?.includes(
          "Failed to fetch dynamically imported module",
        ) ||
        this.state.error?.message?.includes("Loading chunk") ||
        this.state.error?.message?.includes("Loading CSS chunk");

      return (
        <div className="flex min-h-screen items-center justify-center bg-background px-4">
          <div className="mx-auto max-w-md text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangle className="h-7 w-7 text-destructive" />
            </div>
            <h1 className="text-xl font-semibold">
              {isChunkError ? "Update available" : "Something went wrong"}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {isChunkError
                ? "A new version of Relay has been deployed. Reload to get the latest."
                : "An unexpected error occurred. You can try again or reload the page."}
            </p>
            <div className="mt-6 flex items-center justify-center gap-3">
              {!isChunkError && (
                <Button variant="outline" onClick={this.handleRetry}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Try again
                </Button>
              )}
              <Button onClick={this.handleReload}>Reload page</Button>
            </div>
            {import.meta.env.DEV && this.state.error && (
              <pre className="mt-6 max-h-40 overflow-auto rounded-md bg-muted p-3 text-left text-xs text-muted-foreground">
                {this.state.error.message}
                {"\n"}
                {this.state.error.stack}
              </pre>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
